import React, { useEffect, useRef } from 'react';
import {
  Col,
  Row,
  Tooltip as BootstrapTooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import {
  Spinner,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Field, FieldArray } from 'redux-form';
import { isNil, pathOr } from 'ramda';
import { useFlags } from 'launchdarkly-react-client-sdk';

import * as hooks from './hooks';

import {
  renderCurrencyField,
  renderSimpleCheckboxField,
  renderCLCurrencyField,
} from '../../../../../utils/redux-form-helper';
import { formatCurrency } from '../../../../../utils';

import EditLeaseDataLink from '../../EditLeaseDataLink';
import EditLinkWithPermissions from '../../../../../components/EditLinkWithPermissions';
import generalMessages from '../../../../App/messages';
import componentMessages from './messages';
import leaseDataTabFormMessages from '../../messages';
import { recalculateProratedAmountsForRentChange } from '../utils';

const messages = {
  ...generalMessages,
  ...componentMessages,
  ...leaseDataTabFormMessages,
};

const FeeRow = ({ idx, fee }: any) => (
  <Row key={idx}>
    <Col xs={7}>
      <label>{fee.label}</label>
    </Col>
    <Col xs={5}>
      <div className="form-group">
        {fee.value !== 0 ? (
          <FormattedNumber
            value={fee.value}
            style={{ value: 'currency' }.value}
            currency="USD"
          />
        ) : (
          fee.descriptiveValue
        )}
      </div>
    </Col>
  </Row>
);
export const LeaseMonthlyRequiredFees = ({
  monthlyRecurringCharges,
}: Object) => (
  <React.Fragment>
    {monthlyRecurringCharges.map((fee, index) => {
      return <FeeRow fee={fee} idx={index} key={index}></FeeRow>;
    })}
  </React.Fragment>
);

// providerCaption is a plain Field prop, never closed over, so `component` keeps the same
// function identity across renders — redux-form remounts (dropping focus mid-typing) otherwise.
const renderLeaseRentCLField = ({ providerCaption, ...fieldProps }) => {
  const { meta } = fieldProps;
  const caption = !meta.error && providerCaption;
  return renderCLCurrencyField({
    ...fieldProps,
    helperText: meta.error || providerCaption || ' ',
    FormHelperTextProps: caption
      ? { 'data-test': 'lease-rent-provider' }
      : undefined,
  });
};

// Legacy path has no MUI helper-text slot, so the caption renders as a separate element.
const renderLeaseRentLegacyField = ({ providerCaption, ...fieldProps }) => {
  const { meta } = fieldProps;
  const caption = !meta.error && providerCaption;
  return (
    <>
      {renderCurrencyField(fieldProps)}
      {caption && (
        <div className="text-gray--darker" data-test="lease-rent-provider">
          {providerCaption}
        </div>
      )}
    </>
  );
};

const getLeaseRentField = ({
  leaseRentIsEditable,
  isHUD,
  isRD,
  isPH,
  formValues,
  onChangeLeaseRentAmount,
  intl,
  flags,
  providerCaption,
}) => {
  const { automatedLeaseChargesP1 } = flags;
  const renderLeaseRentInput = automatedLeaseChargesP1
    ? renderLeaseRentCLField
    : renderLeaseRentLegacyField;

  if (isPH) {
    return (
      <Tooltip
        title={intl.formatMessage(messages.phLeaseRentTooltip)}
        placement="top"
      >
        <Field
          name="leasedRent"
          component={renderLeaseRentInput}
          providerCaption={providerCaption}
          step="0.01"
          disabled={true}
        />
      </Tooltip>
    );
  }

  return (
    <>
      {!leaseRentIsEditable && (isHUD || isRD || isPH) ? (
        <Tooltip
          title={intl.formatMessage(messages.hudRdFloorPlanUpdateTooltip)}
          placement="top"
        >
          <div>
            <Field
              name="leasedRent"
              component={renderLeaseRentInput}
              providerCaption={providerCaption}
              step="0.01"
              disabled={!leaseRentIsEditable}
              onChange={onChangeLeaseRentAmount(formValues.startDate)}
            />
          </div>
        </Tooltip>
      ) : (
        <Field
          name="leasedRent"
          component={renderLeaseRentInput}
          providerCaption={providerCaption}
          step="0.01"
          disabled={!leaseRentIsEditable}
          onChange={onChangeLeaseRentAmount(formValues.startDate)}
        />
      )}
    </>
  );
};

export const MonthlyLeaseCharges = ({
  monthlyRecurringCharges,
  intl,
  isAffordable,
  applicableRents,
  isEditable,
  leaseRentIsEditable,
  marketRent,
  nonRentCharges,
  quotingRent,
  recommendedRentProviderName,
  clearRecommendedRent,
  noteRent,
  basicRent,
  affordableValues,
  showEditLink,
  showLeaseDataModal,
  showLeaseReqFeesModal,
  changeFn,
  monthlyRequiredFeesEditable = true,
  isDisabledMonthly,
  lease,
  canComplete,
  selectedProperty,
  nonOptionalCharge,
  isHUD = false,
  isRD = false,
  isPH = false,
  isSection236,
  hud236BasicRent,
  hud236MarketRent,
  formValues,
  setProratedRentAmount,
  setProratedOtherAmount,
  resetProratedRentAutoFilledChangeStatus,
  householdRentableItems = [],
  petCount = 0,
  isShortTermLease = false,
}: Object) => {
  // Use ref on the dependency that caused re-renders
  const changeFnRef = useRef(changeFn);

  useEffect(() => {
    changeFnRef.current = changeFn;
  }, [changeFn]);

  useEffect(() => {
    // Set quoting and market rent from either lease or floorplan values
    // These values only need to be set once when the component mounts
    changeFnRef.current('marketRentValue', marketRent);
    changeFnRef.current('quotingRentValue', quotingRent);
    changeFnRef.current('nonOptionalCharge', nonOptionalCharge);

    // Affordable rents trigger a second render since they are async loaded
    changeFnRef.current('basicRent', basicRent);
    changeFnRef.current('noteRent', noteRent);
    if (isSection236) {
      changeFnRef.current('hud236BasicRent', hud236BasicRent);
      changeFnRef.current('hud236MarketRent', hud236MarketRent);
    }
  }, [
    marketRent,
    quotingRent,
    basicRent,
    noteRent,
    nonOptionalCharge,
    isSection236,
    hud236BasicRent,
    hud236MarketRent,
    affordableValues.utilityAllowanceAmount,
  ]);

  const { automatedLeaseChargesP1 } = useFlags();
  const leaseId = pathOr('', ['id'], lease);
  const [updateReceivesHousingAssistance] =
    hooks.useUpdateReceivesHousingAssistance(
      pathOr('', ['organizationId'], selectedProperty),
      pathOr('', ['id'], selectedProperty),
      leaseId,
    );
  const { utilityAllowanceAmount, utilityAllowanceName } = affordableValues;
  const { isLocked, isMovedIn, lockedFees } = lease;
  const notCurrentMovedInLease = isLocked && !isMovedIn;
  const disableEditFeesLink =
    !canComplete || isDisabledMonthly || notCurrentMovedInLease || !lockedFees;

  const isLeaseCreated = typeof leaseId === 'string' && leaseId.length > 0;

  const handleIsReceivingAssistanceChange = (e) => {
    if (isLeaseCreated) {
      const currentValue = e.target.value === 'true';
      updateReceivesHousingAssistance(!currentValue);
    }
  };

  const nonRentChargesTooltip = (
    <BootstrapTooltip key={'tooltip-nrc'} id={'tooltip-nrc'}>
      <FormattedMessage {...messages.tooltipNRC} />
    </BootstrapTooltip>
  );

  const onChangeLeaseRentAmount = (startDate) => (event, newValue) => {
    const value =
      newValue ??
      (typeof event === 'object' && event !== null
        ? event?.target?.value
        : event);
    // FINDING 8 — this is a genuine manual edit, so whatever REBA recommendation is currently
    // applied no longer describes what's in the field (it was priced for whichever inputs were
    // selected when it was applied, not for whatever the user just typed). Clear it unconditionally
    // — the label isn't gated behind automatedLeaseChargesP1, so neither is this.
    clearRecommendedRent?.();
    if (automatedLeaseChargesP1) {
      recalculateProratedAmountsForRentChange({
        rentAmount: value,
        startDate,
        monthlyRecurringCharges,
        formValues,
        petCount,
        isShortTermLease,
        householdRentableItems,
        setProratedRentAmount,
        setProratedOtherAmount,
        resetProratedRentAutoFilledChangeStatus,
        change: changeFnRef.current,
      });
    }
  };

  const leaseRentProviderCaption = recommendedRentProviderName
    ? intl.formatMessage(messages.poweredByProvider, {
        provider: recommendedRentProviderName,
      })
    : null;

  const leaseRentAmount = getLeaseRentField({
    leaseRentIsEditable: leaseRentIsEditable ?? isEditable,
    isHUD,
    isRD,
    isPH,
    formValues,
    onChangeLeaseRentAmount,
    intl,
    flags: {
      automatedLeaseChargesP1,
    },
    providerCaption: leaseRentProviderCaption,
  });

  return (
    <div>
      <Row>
        <Col xs={12}>
          <h2>
            <i className="et-money text-blue padright10" />
            <FormattedMessage {...messages.monthlyLeaseCharges} />
          </h2>
        </Col>
      </Row>
      <Col xs={7} sm={6}>
        <Row>
          <Col xs={12} sm={6}>
            <h3 className="padtop10">
              <FormattedMessage {...messages.rentCharges} />
            </h3>
          </Col>
          <form>
            {isAffordable && isRD && applicableRents?.noteRent && (
              <Row>
                <Col xs={7}>
                  <label>{intl.formatMessage(messages.noteRent)}</label>
                </Col>
                <Col xs={4}>
                  <Field
                    name="noteRent"
                    component={renderCurrencyField}
                    step="0.01"
                    disabled={true}
                  />
                </Col>
              </Row>
            )}
            {isAffordable && isRD && applicableRents?.basicRent && (
              <Row>
                <Col xs={7}>
                  <label>{intl.formatMessage(messages.basicRent)}</label>
                </Col>
                <Col xs={4}>
                  <Field
                    name="basicRent"
                    component={renderCurrencyField}
                    step="0.01"
                    disabled={true}
                  />
                </Col>
              </Row>
            )}
            {isSection236 && [
              <Row>
                <Col xs={7}>
                  <label>{intl.formatMessage(messages.hud236BasicRent)}</label>
                </Col>
                <Col xs={4}>
                  <Field
                    name="hud236BasicRent"
                    component={renderCurrencyField}
                    step="0.01"
                    disabled={true}
                  />
                </Col>
              </Row>,
              <Row>
                <Col xs={7}>
                  <label>{intl.formatMessage(messages.hud236MarketRent)}</label>
                </Col>
                <Col xs={4}>
                  <Field
                    name="hud236MarketRent"
                    component={renderCurrencyField}
                    step="0.01"
                    disabled={true}
                  />
                </Col>
              </Row>,
            ]}
            {!isSection236 && (
              <Row>
                <Col xs={7}>
                  <label>
                    {intl.formatMessage(
                      isAffordable
                        ? applicableRents?.hudGrossRent && isHUD
                          ? messages.hudGrossRentLimit
                          : messages.grossRentLimit
                        : messages.marketRentAmount,
                    )}
                  </label>
                </Col>
                <Col xs={4}>
                  <div className="form-group">
                    <Field
                      name="marketRentValue"
                      component={renderCurrencyField}
                      step="0.01"
                      disabled={true}
                    />
                  </div>
                </Col>
              </Row>
            )}
            {utilityAllowanceName && (
              <Row>
                <Col xs={7}>
                  <label>
                    {utilityAllowanceName}{' '}
                    {intl.formatMessage(messages.utilityAllowance)}
                  </label>
                </Col>
                <Col xs={4}>
                  {isNil(utilityAllowanceAmount) ? (
                    <Spinner small />
                  ) : (
                    <div className="form-group">
                      <Field
                        name="utilityAllowanceAmount"
                        component={renderCurrencyField}
                        step="0.01"
                        disabled={true}
                      />
                    </div>
                  )}
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={7}>
                <label>{intl.formatMessage(messages.quotingRentAmount)}</label>
              </Col>
              <Col xs={4}>
                <div className="form-group">
                  <Field
                    name="quotingRentValue"
                    component={renderCurrencyField}
                    step="0.01"
                    disabled={true}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={7}>
                <label className="required">
                  {intl.formatMessage(messages.leaseRentAmount)}
                </label>
              </Col>
              <Col xs={4}>
                <div className="form-group">{leaseRentAmount}</div>
              </Col>
              {!isPH && (
                <Col xs={1}>
                  <EditLeaseDataLink
                    showEditLink={showEditLink}
                    showLeaseDataModal={showLeaseDataModal}
                  />
                </Col>
              )}
            </Row>
            {isAffordable && (
              <Row>
                <Col xs={7}>
                  <label>
                    {intl.formatMessage(messages.receivesHousingAssistance)}
                  </label>
                </Col>
                <Col xs={5}>
                  <Field
                    name="isReceivingAssistance"
                    component={renderSimpleCheckboxField}
                    label="Yes"
                    onChange={handleIsReceivingAssistanceChange}
                    disabled={!isEditable}
                  />
                </Col>
              </Row>
            )}
          </form>

          <label style={{ marginRight: '10px' }}>
            <FormattedMessage {...messages.requiredNonRentCharges} />
          </label>

          {isEditable && (
            <OverlayTrigger
              key={'tooltip-overlay'}
              placement="bottom"
              delayHide={50}
              trigger={['hover', 'focus']}
              overlay={nonRentChargesTooltip}
            >
              <i className="icon et-alert-info padright10" />
            </OverlayTrigger>
          )}

          {!disableEditFeesLink && (
            <EditLinkWithPermissions
              showEditLink={isEditable}
              linkFn={showLeaseReqFeesModal}
              scope={['lease-edit-fees']}
              disabled={disableEditFeesLink}
            />
          )}
          <div className="form-group form-groupbox form-groupbox--scrollable no-margin-bottom">
            <FieldArray
              name="monthlyReqFees"
              component={LeaseMonthlyRequiredFees}
              monthlyRecurringCharges={monthlyRecurringCharges}
            ></FieldArray>
          </div>
          <div className="highlighted-footer">
            <Row>
              <Col xs={7}>
                <span>
                  <FormattedMessage {...messages.totalCharges} />
                </span>
              </Col>
              <Col xs={5}>
                <span>{formatCurrency(intl, nonRentCharges)}</span>
              </Col>
            </Row>
          </div>
        </Row>
      </Col>
    </div>
  );
};

export default MonthlyLeaseCharges;
